import React, { useEffect, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import { Popover, PopoverHandler, PopoverContent, Button } from '@material-tailwind/react';
import '../DateTimePicker.css';

export interface Time {
  hour: string;
  minute: string;
  format?: 'AM' | 'PM';
}

interface TimeOptionProps {
  id: string;
  value: string;
  isSelected: boolean;
  onClick: () => void;
  isLocked?: boolean;
  initialSelected?: string;
}

const TimeOption: React.FC<TimeOptionProps> = ({ id, value, isSelected, onClick, initialSelected, isLocked }) => {
  const defaultSelected = initialSelected === value;

  return (
    <span
      id={id}
      onClick={() => !isLocked && onClick()}
      className={`cursor-pointer p-2 rounded-lg text-neutral-grey900 ${
        isSelected ? 'bg-blue-500 text-white' : `${defaultSelected ? 'hover:bg-blue-500' : 'hover:bg-gray-200'}`
      } ${defaultSelected && 'bg-blue-500 text-white hover:bg-blue-500 hover:text-white'} ${
        isLocked ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      {value}
    </span>
  );
};

interface TimeSectionProps {
  id: string;
  initialTime: Time;
  onTimeChange: (time: Time) => void;
  // Flag to update the time when the user selects a new date range
  updateTime: boolean;
  timeFormat: '12' | '24';
  disabled?: boolean;
  isLocked?: boolean;
}

const TimeSection: React.FC<TimeSectionProps> = ({
  id,
  initialTime,
  onTimeChange,
  updateTime,
  timeFormat,
  disabled,
  isLocked
}) => {
  const [time, setTime] = useState<Time>(initialTime);
  const [isOpen, setIsOpen] = useState(false);

  // Update the time format when the user selects a new format
  const handleFormatChange = (value: 'AM' | 'PM') => {
    if (isLocked) return;
    const newTime = { ...time, format: value };
    setTime(newTime);
    onTimeChange(convertTo24HourFormat(newTime));
  };

  //  Update the time when the user selects a new time
  const handleTimeChange = (type: keyof Time, value: string) => {
    if (isLocked) return;
    const newTime = { ...time, [type]: value };
    setTime(newTime);
    const convertedTime = convertTo24HourFormat(newTime);
    // Send the new time to the parent component
    onTimeChange(convertedTime);
  };

  // Update the time when the user inputs a new time
  const handleInputChange = (value: string) => {
    if (isLocked) return;
    const [hour = '', minute = ''] = value.includes(':') ? value.split(':') : [value, ''];
    const newTime = { hour: hour.padStart(2, '0'), minute: minute.padStart(2, '0') };
    setTime(newTime);
    onTimeChange(newTime);
  };

  // Rerender the component when the user selects a new date range
  useEffect(() => {
    setTime(initialTime);
  }, [updateTime]);

  // Render the time options for the hour and minute
  const renderOptions = (start: number, end: number, type: keyof Time) => {
    const options = [];
    // Create the options for the hour and minute
    for (let i = start; i <= end; i++) {
      // Format the value to have two digits
      const formattedValue = i < 10 ? `0${i}` : `${i}`;
      options.push(
        <TimeOption
          id={`${id}-${type}-item-${i}`}
          key={formattedValue}
          value={formattedValue}
          isSelected={time[type] === formattedValue}
          onClick={() => handleTimeChange(type, formattedValue)}
          isLocked={isLocked}
        />
      );
    }
    return options;
  };

  const toggleOpen = () => {
    if (!isLocked) setIsOpen(!isOpen);
  };

  const getInitialAmPmTimeFormat = () => (parseInt(initialTime.hour) < 12 ? 'AM' : 'PM');

  // Convert the time to 24-hour format and pass it to the parent component
  const convertTo24HourFormat = (time: Time): Time => {
    if (timeFormat === '12' && time.format) {
      let hour = parseInt(time.hour, 10);
      if (time.format === 'PM' && hour !== 12) hour += 12;
      if (time.format === 'AM' && hour === 12) hour = 0;
      return { hour: hour.toString().padStart(2, '0'), minute: time.minute };
    }
    return time;
  };

  return (
    <div className="flex items-center">
      <Popover placement="bottom" open={isOpen} handler={toggleOpen}>
        <PopoverHandler>
          <Button
            id={`${id}-select`}
            disabled={isLocked || disabled}
            variant="text"
            size="sm"
            className={`p-1 mr-1 ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
            placeholder=""
            onResize={() => {}}
            onResizeCapture={() => {}}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          >
            <FiClock className="text-blue-500 w-[20px] h-[20px] cursor-pointer" />
          </Button>
        </PopoverHandler>
        <PopoverContent 
          className={`z-[999999] p-0 ${isLocked ? 'pointer-events-none opacity-50' : ''}`}
          placeholder=""
          onResize={() => {}}
          onResizeCapture={() => {}}
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <div className="shadow-[1px_1px_9px_0.5px_rgba(0,0,0,0.1)] p-4">
            <div className="flex gap-2">
              <div className="w-[60px] text-center">
                <span className="text-blue-500 mb-2">Hour</span>
                <div className="flex flex-col gap-1 h-[160px] max-h-[160px] overflow-y-auto no-scrollbar">
                  {timeFormat === '24' ? renderOptions(0, 23, 'hour') : renderOptions(1, 12, 'hour')}
                </div>
              </div>
              <div className="w-[60px] text-center">
                <span className="text-blue-500 mb-3">Minutes</span>
                <div className="flex flex-col gap-1 h-[160px] max-h-[160px] overflow-y-auto no-scrollbar">
                  {renderOptions(0, 59, 'minute')}
                </div>
              </div>
              {timeFormat === '12' && (
                <div className="w-[60px] text-center">
                  <span className="text-blue-500 mb-3">Format</span>
                  <div className="flex flex-col gap-1 h-[160px] max-h-[160px] overflow-y-auto no-scrollbar">
                    {['AM', 'PM'].map((value, index) => (
                      <TimeOption
                        id={`${id}-item-${index}`}
                        key={value}
                        value={value}
                        isLocked={isLocked}
                        initialSelected={getInitialAmPmTimeFormat()}
                        isSelected={time.format === value}
                        onClick={() => handleTimeChange('format', value)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="text-right m-0">
              <Button
                id={`${id}-apply`}
                variant="text"
                size="sm"
                disabled={isLocked}
                className={`text-blue-500 p-3 m-0 ${isLocked ? 'cursor-not-allowed opacity-50' : 'hover:bg-neutral-grey50'}`}
                onClick={toggleOpen}
                placeholder=""
                onResize={() => {}}
                onResizeCapture={() => {}}
                onPointerEnterCapture={() => {}}
                onPointerLeaveCapture={() => {}}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <input
        id={`${id}-input`}
        type="time"
        value={`${time.hour.padStart(2, '0')}:${time.minute.padStart(2, '0')}`}
        step={60}
        disabled={disabled || isLocked}
        onChange={(e) => handleInputChange(e.target.value)}
        className={`time-input cursor-pointer ${timeFormat === '12' ? 'w-[60px]' : 'w-[80px]'} ${
          isLocked ? 'cursor-not-allowed opacity-50' : ''
        }`}
      />

      {timeFormat === '12' && (
        <select
          id={`${id}-select-format`}
          value={time.format || getInitialAmPmTimeFormat()}
          disabled={isLocked}
          onChange={(e) => handleFormatChange(e.target.value as 'AM' | 'PM')}
          className="bg-white text-center text-[15px] py-[3px] px-[2px] border border-[#cbd5e0] rounded-[5px] outline-none text-[#1a1919] cursor-pointer ml-1"
        >
          <option id={`${id}-format-am`} value="AM">
            AM
          </option>
          <option id={`${id}-format-pm`} value="PM">
            PM
          </option>
        </select>
      )}
    </div>
  );
};

export default TimeSection; 